import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TabsNavbar from "../Common/TabsNavBar/TabsNavbar";
import AppCustomersTable from "./AppCustomersTable";
import AppCustomerStats from "./AppCustomerStats";
import AppCustomerMap from "./AppCustomerMap";
import CompetitorsTable from "./Competitors/CompetitorsTable";
import CommercialsTable from "./Commercials/CommercialsTable";
import ProjectsTab from "./Projects";
import CustomerSpaceFilters from "./CustomerSpaceFilters";
import ProjectModal from "./Projects/ProjectModal";
import { openRightModal } from "../../REDUX/actions/rightModal";

// Tab labels are raw i18n keys — TabsNavbar translates internally.
const TABS = [
  "Client",
  "Prospect",
  "Project",
  "Competitor",
  "Commercial",
  "Map",
];

const AppCustomersViewContent = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Client");

  // Deep link (toast "Ouvrir le projet N°…", order "Liens"): navigate here
  // with state { projectId, projectTab } to open that project's sheet.
  useEffect(() => {
    const { projectId, projectTab } = location.state ?? {};
    if (!projectId) return;
    setActiveTab("Project");
    dispatch(
      openRightModal(
        // keyed by the navigation: an already open sheet remounts (refetch, tab).
        <ProjectModal
          key={location.key}
          projectId={projectId}
          initialTab={projectTab}
        />,
      ),
    );
    // Consumed: a reload / back must not reopen it.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.key, location.pathname, dispatch, navigate]);
  // A scope change (filter bar) remounts the tab so every list, counter and
  // map refetches with it.
  const { scope } = useSelector((state) => state.appCustomersReducer);
  // A store's commercial doesn't manage the store's commercials.
  const isCommercial =
    useSelector((state) => state.userReducer.user?.attributes?.userRole) === 7;
  const tabs = isCommercial ? TABS.filter((tab) => tab !== "Commercial") : TABS;
  const scopeKey = `${scope.organizationId ?? ""}-${scope.businessAffiliateId ?? ""}`;

  return (
    <div className="d-flex flex-column gap-4">
      <TabsNavbar
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3">
        <h2 className="quickSand f30 bold text-blue mb-0">
          {t(`${activeTab}`)}
        </h2>
        <CustomerSpaceFilters />
      </div>
      {activeTab === "Map" ? (
        <AppCustomerMap key={scopeKey} />
      ) : activeTab === "Competitor" ? (
        <CompetitorsTable key={scopeKey} />
      ) : activeTab === "Commercial" ? (
        <CommercialsTable key={scopeKey} />
      ) : activeTab === "Project" ? (
        <ProjectsTab key={scopeKey} />
      ) : (
        <>
          {/* key remounts stats + table so they refetch with the tab's role */}
          <AppCustomerStats
            key={`stats-${activeTab}-${scopeKey}`}
            role={activeTab}
          />
          <AppCustomersTable
            key={`${activeTab}-${scopeKey}`}
            role={activeTab}
          />
        </>
      )}
    </div>
  );
};

export default AppCustomersViewContent;
